import { useTranslation } from 'react-i18next'
import { v4 as uuidv4 } from 'uuid'
import { useCacheHelpers } from '../apollo/cache-helpers'
import {
  CreateTemperatureCurveInput,
  ProjectTemperatureCurvesFragment,
  ProjectTemperatureCurvesFragmentDoc,
  TemperatureCurveFragment,
  TemperatureCurveFragmentDoc,
  UpdateTemperatureCurveInput,
  useCreateTemperatureCurveMutation,
  useUpdateTemperatureCurveMutation,
  useDeleteTemperatureCurveMutation,
  ProjectFragment,
  ProjectFragmentDoc,
  TemperatureCurveType,
} from '../generated/graphql'
import { useNotifications } from '../notifications/use-notifications'

type UseTemperatureCurves = {
  createTemperatureCurve: (
    projectId: string,
    input: CreateTemperatureCurveInput,
  ) => Promise<TemperatureCurveFragment>
  updateTemperatureCurve: (
    id: string,
    projectId: string,
    input: UpdateTemperatureCurveInput,
  ) => Promise<TemperatureCurveFragment>
  deleteTemperatureCurve: (id: string, projectId: string) => Promise<boolean>
}

export function useTemperatureCurves(): UseTemperatureCurves {
  const [createTemperatureCurveMutation] = useCreateTemperatureCurveMutation()
  const [updateTemperatureCurveMutation] = useUpdateTemperatureCurveMutation()
  const [deleteTemperatureCurveMutation] = useDeleteTemperatureCurveMutation()
  const { addItemToList, removeItemFromList, getFragment } = useCacheHelpers()
  const { enqueueError } = useNotifications()
  const { t } = useTranslation()

  const checkForDuplicateName = (
    newName: string,
    project: ProjectTemperatureCurvesFragment,
  ) => {
    const { temperatureCurves } = project
    if (temperatureCurves.some(({ name }) => name === newName)) {
      enqueueError(t('temperatureCurve.conflictingNameErrorMessage'))
      throw new Error(
        `Project ${project.id} already has temperature curve with name ${newName}. Please choose another name.`,
      )
    }
  }

  const createTemperatureCurve = async (
    projectId: string,
    input: CreateTemperatureCurveInput,
  ) => {
    const project = getFragment<ProjectTemperatureCurvesFragment>(
      projectId,
      ProjectTemperatureCurvesFragmentDoc,
    )
    checkForDuplicateName(input.name, project)

    const result = await createTemperatureCurveMutation({
      variables: { projectId, input },

      update: (_, mutationResult) => {
        const curve = mutationResult.data?.createProjectSpecificTemperatureCurve
        if (!curve) {
          return
        }

        addItemToList(
          TemperatureCurveFragmentDoc,
          curve,
          project,
          'temperatureCurves',
        )
      },

      optimisticResponse: {
        createProjectSpecificTemperatureCurve: {
          __typename: 'TemperatureCurve',
          id: uuidv4(),
          type: TemperatureCurveType.ProjectSpecific,
          name: input.name,
          table: input.table,
        },
      },
    })

    if (!result.data) {
      throw new Error('Temperature curve creation failed')
    }

    return result.data.createProjectSpecificTemperatureCurve
  }

  const updateTemperatureCurve = async (
    id: string,
    projectId: string,
    input: UpdateTemperatureCurveInput,
  ) => {
    const curve = getFragment<TemperatureCurveFragment>(
      id,
      TemperatureCurveFragmentDoc,
    )

    if (input.name && curve.name !== input.name) {
      const project = getFragment<ProjectTemperatureCurvesFragment>(
        projectId,
        ProjectTemperatureCurvesFragmentDoc,
      )
      checkForDuplicateName(input.name, project)
    }

    const result = await updateTemperatureCurveMutation({
      variables: { id, input },

      optimisticResponse: {
        updateTemperatureCurve: {
          ...curve,
          name: input.name ?? curve.name,
          table: input.table ?? curve.table,
        },
      },
    })

    if (!result.data) {
      throw new Error('Temperature curve update failed')
    }

    return result.data.updateTemperatureCurve
  }

  const deleteTemperatureCurve = async (id: string, projectId: string) => {
    const project = getFragment<ProjectFragment>(projectId, ProjectFragmentDoc)

    const curveIsInUse = project.versions
      .flatMap(({ buildings, localGrids }) => [...buildings, ...localGrids])
      .flatMap(({ providers }) => providers)
      .flatMap(({ temperatureCurves }) => temperatureCurves)
      .some((curve) => curve.id === id)

    if (curveIsInUse) {
      enqueueError(t('temperatureCurve.curveInUseErrorMessage'))
      throw new Error(
        `Temperature curve ${id} is in use and it can not be removed.`,
      )
    }

    const result = await deleteTemperatureCurveMutation({
      variables: { id },

      update: (_, mutationResult) => {
        const success = mutationResult.data?.deleteTemperatureCurve
        if (!success) {
          return
        }
        const projectTemperatureCurves =
          getFragment<ProjectTemperatureCurvesFragment>(
            projectId,
            ProjectTemperatureCurvesFragmentDoc,
          )

        removeItemFromList(
          TemperatureCurveFragmentDoc,
          id,
          projectTemperatureCurves,
          'temperatureCurves',
        )
      },

      optimisticResponse: { deleteTemperatureCurve: true },
    })

    if (!result.data) {
      throw new Error('Temperature curve delete failed')
    }

    return result.data.deleteTemperatureCurve
  }

  return {
    createTemperatureCurve,
    updateTemperatureCurve,
    deleteTemperatureCurve,
  }
}
